
import React, { useState } from 'react';
import { Bot, Users, Zap, Activity, ArrowDown, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Agent data with enhanced details
const agents = [
  {
    id: 'executive',
    name: 'Executive Agent',
    description: 'Orchestrates all recruitment activities and makes strategic decisions',
    category: 'executive',
    tier: 'Executive',
    status: 'active',
    connections: ['recruiting-1', 'recruiting-2'],
    capabilities: ['Strategy', 'Coordination', 'Decision Making'],
    activeJobs: 12,
    successRate: 94
  },
  {
    id: 'recruiting-1',
    name: 'Senior Recruiting Agent',
    description: 'Specialized in executive and senior-level recruitment',
    category: 'recruiting',
    tier: 'Senior',
    status: 'active',
    connections: ['specialized-1', 'specialized-2'],
    capabilities: ['Executive Search', 'Relationship Building', 'Market Research'],
    activeJobs: 8,
    successRate: 89
  },
  {
    id: 'recruiting-2',
    name: 'Technical Recruiting Agent',
    description: 'Focuses on technical roles and engineering positions',
    category: 'recruiting',
    tier: 'Senior',
    status: 'active',
    connections: ['specialized-3', 'specialized-4'],
    capabilities: ['Technical Assessment', 'Skill Matching', 'Industry Knowledge'],
    activeJobs: 15,
    successRate: 91
  },
  {
    id: 'specialized-1',
    name: 'Screening Agent',
    description: 'Automated candidate screening and initial assessments',
    category: 'specialized',
    tier: 'Specialized',
    status: 'active',
    connections: [],
    capabilities: ['Resume Parsing', 'Skill Assessment', 'Cultural Fit'],
    activeJobs: 32,
    successRate: 87
  },
  {
    id: 'specialized-2',
    name: 'Communication Agent',
    description: 'Manages candidate communications and follow-ups',
    category: 'specialized',
    tier: 'Specialized',
    status: 'active',
    connections: [],
    capabilities: ['Email Automation', 'Scheduling', 'Updates'],
    activeJobs: 28,
    successRate: 92
  },
  {
    id: 'specialized-3',
    name: 'Analytics Agent',
    description: 'Provides recruitment insights and performance analytics',
    category: 'specialized',
    tier: 'Specialized',
    status: 'active',
    connections: [],
    capabilities: ['Data Analysis', 'Reporting', 'Insights'],
    activeJobs: 0,
    successRate: 96
  },
  {
    id: 'specialized-4',
    name: 'Research Agent',
    description: 'Conducts market research and candidate sourcing',
    category: 'specialized',
    tier: 'Specialized',
    status: 'active',
    connections: [],
    capabilities: ['Market Research', 'Sourcing', 'Competitive Analysis'],
    activeJobs: 18,
    successRate: 88
  }
];

// Category color mapping using semantic colors
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'executive':
      return 'bg-primary/10 text-primary border-primary/20';
    case 'recruiting':
      return 'bg-secondary/10 text-secondary border-secondary/20';
    case 'specialized':
      return 'bg-accent-emerald/10 text-accent-emerald border-accent-emerald/20';
    default:
      return 'bg-muted/10 text-muted-foreground border-muted/20';
  }
};

// Agent card component
const AgentCard = ({ agent, isSelected, onSelect }: any) => {
  const categoryColors = getCategoryColor(agent.category);
  
  return (
    <div 
      className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary bg-card' : 'bg-card border-border'
      } ${agent.id === 'executive' ? 'bg-gradient-to-br from-primary/5 to-primary/10' : ''}`}
      onClick={() => onSelect(agent)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className={`p-2 rounded-lg ${categoryColors}`}>
            <Bot className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-medium text-card-foreground">{agent.name}</h3>
            <Badge variant="outline" className={`text-xs ${categoryColors}`}>
              {agent.tier}
            </Badge>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-xs text-accent-emerald font-medium">Active</span>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground mb-3">{agent.description}</p>
      
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Active Jobs:</span>
          <span className="font-medium text-card-foreground">{agent.activeJobs}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Success Rate:</span>
          <span className="font-medium text-card-foreground">{agent.successRate}%</span>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-border">
        <div className="flex flex-wrap gap-1">
          {agent.capabilities.slice(0, 2).map((capability: string) => (
            <span key={capability} className="text-xs px-2 py-1 bg-muted rounded text-muted-foreground">
              {capability}
            </span>
          ))}
          {agent.capabilities.length > 2 && (
            <span className="text-xs text-muted-foreground">+{agent.capabilities.length - 2}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Connection line component
const ConnectionLine = ({ from, to, isHorizontal = false }: any) => {
  const baseClasses = "absolute pointer-events-none";
  
  if (isHorizontal) {
    return (
      <div className={`${baseClasses} h-0.5 bg-gradient-to-r from-primary/30 to-muted`} 
           style={{ 
             top: '50%', 
             left: '100%', 
             width: '2rem',
             transform: 'translateY(-50%)'
           }} 
      />
    );
  }
  
  return (
    <div className={`${baseClasses} w-0.5 bg-gradient-to-b from-muted to-secondary/30`}
         style={{ 
           left: '50%', 
           top: '100%', 
           height: '2rem',
           transform: 'translateX(-50%)'
         }} 
    />
  );
};

export const MarketingAgentHierarchy = () => {
  const [selectedAgent, setSelectedAgent] = useState(agents[0]);
  const [showConnections, setShowConnections] = useState(true);

  // Group agents by category for layout
  const executiveAgents = agents.filter(agent => agent.category === 'executive');
  const recruitingAgents = agents.filter(agent => agent.category === 'recruiting');
  const specializedAgents = agents.filter(agent => agent.category === 'specialized');

  return (
    <div className="bg-gradient-to-br from-background to-card p-6 rounded-xl border border-border">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-foreground">Live Agent Hierarchy</h3>
            <p className="text-muted-foreground">Interactive orchestration network</p>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setShowConnections(!showConnections)}
              className="text-sm px-3 py-1 rounded bg-muted text-muted-foreground hover:bg-secondary/10 transition-colors"
            >
              {showConnections ? 'Hide' : 'Show'} Connections
            </button>
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-accent-emerald" />
              <span className="text-sm font-medium text-foreground">All Systems Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hierarchical Layout */}
      <div className="space-y-8">
        {/* Executive Level */}
        <div className="text-center">
          <div className="inline-block relative">
            {executiveAgents.map((agent) => (
              <div key={agent.id} className="relative">
                <AgentCard 
                  agent={agent} 
                  isSelected={selectedAgent?.id === agent.id}
                  onSelect={setSelectedAgent}
                />
                {showConnections && (
                  <ConnectionLine from={agent.id} to="recruiting" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recruiting Level */}
        <div className="flex justify-center space-x-8">
          {recruitingAgents.map((agent, index) => (
            <div key={agent.id} className="relative">
              <AgentCard 
                agent={agent} 
                isSelected={selectedAgent?.id === agent.id}
                onSelect={setSelectedAgent}
              />
              {showConnections && (
                <ConnectionLine from={agent.id} to="specialized" />
              )}
            </div>
          ))}
        </div>

        {/* Specialized Level */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {specializedAgents.map((agent) => (
            <AgentCard 
              key={agent.id}
              agent={agent} 
              isSelected={selectedAgent?.id === agent.id}
              onSelect={setSelectedAgent}
            />
          ))}
        </div>
      </div>

      {/* Selected Agent Details */}
      {selectedAgent && (
        <div className="mt-6 p-4 bg-card rounded-lg border border-border">
          <div className="flex items-center space-x-3 mb-3">
            <div className={`p-2 rounded-lg ${getCategoryColor(selectedAgent.category)}`}>
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <h4 className="font-medium text-card-foreground">{selectedAgent.name}</h4>
              <p className="text-sm text-muted-foreground">{selectedAgent.description}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-primary">{selectedAgent.activeJobs}</div>
              <div className="text-xs text-muted-foreground">Active Jobs</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-secondary">{selectedAgent.successRate}%</div>
              <div className="text-xs text-muted-foreground">Success Rate</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-accent-emerald">{selectedAgent.capabilities.length}</div>
              <div className="text-xs text-muted-foreground">Capabilities</div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-border">
            <h5 className="text-sm font-medium text-card-foreground mb-2">Core Capabilities:</h5>
            <div className="flex flex-wrap gap-2">
              {selectedAgent.capabilities.map((capability) => (
                <span key={capability} className="text-xs px-2 py-1 bg-muted rounded text-muted-foreground">
                  {capability}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Network Stats Footer */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-primary">{agents.length}</div>
            <div className="text-sm text-muted-foreground">Total Agents</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-secondary">{agents.reduce((sum, agent) => sum + agent.activeJobs, 0)}</div>
            <div className="text-sm text-muted-foreground">Active Jobs</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-accent-emerald">{Math.round(agents.reduce((sum, agent) => sum + agent.successRate, 0) / agents.length)}%</div>
            <div className="text-sm text-muted-foreground">Avg Success</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-muted-foreground">24/7</div>
            <div className="text-sm text-muted-foreground">Uptime</div>
          </div>
        </div>
      </div>
    </div>
  );
};
